import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import ABTestCharts from '@/components/ABTestCharts';
import NewVariantForm from '@/components/NewVariantForm';
import { calculateSignificance, findBestVariant, hasEnoughData, generateRecommendations } from '@/utils/abTestAnalyzer';
import { fetchABTestData, setDefaultVariant } from '@/services/abTestService';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';

const ABTestDashboard = () => {
  const [data, setData] = useState<{ totalVisitors: number; variants: any[] }>({ totalVisitors: 0, variants: [] });
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d');
  const [showNewVariantForm, setShowNewVariantForm] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch data when the component mounts or date range changes
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const testData = await fetchABTestData(dateRange);
        setData(testData);
      } catch (error) {
        console.error('Failed to fetch A/B test data:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch A/B test data. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [dateRange, toast]);

  // Handle setting the default variant
  const handleSetDefault = async (variantId: string, variantName: string) => {
    try {
      const result = await setDefaultVariant(variantId);
      if (result.success) {
        toast({
          title: 'Success',
          description: `${variantName} is now the default landing page.`,
        });
      } else {
        toast({
          title: 'Error',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Failed to set default variant:', error);
      toast({
        title: 'Error',
        description: 'Failed to set default variant. Please try again.',
        variant: 'destructive',
      });
    }
  };
  
  // Handle refreshing the data after creating a new variant
  const handleVariantCreated = async () => {
    try {
      const testData = await fetchABTestData(dateRange);
      setData(testData);
      toast({
        title: 'Data Refreshed',
        description: 'A/B test data has been refreshed with the new variant.',
      });
    } catch (error) {
      console.error('Failed to refresh A/B test data:', error);
    }
  };

  // If loading or no data, show loading state
  if (loading || data.variants.length === 0) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Landing Page Dashboard</h1>
            <p className="text-gray-500">Performance metrics for all landing page variations</p>
          </div>
        </div>
        
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading test data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Find the winning variant
  const winningVariant = findBestVariant(data.variants);
  
  // Check if we have enough data
  const enoughData = hasEnoughData(data.variants);
  
  // Get recommendations
  const recommendations = generateRecommendations(data.variants);
  
  // Calculate significance between top two variants
  const sortedVariants = [...data.variants].sort((a, b) => b.conversionRate - a.conversionRate);
  const significance = calculateSignificance(sortedVariants[0], sortedVariants[1]);

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Landing Page Dashboard</h1>
          <p className="text-gray-500">Performance metrics for all landing page variations</p>
        </div>
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate('/')}>
            View Default Page
          </Button>
          <Button variant="outline" onClick={() => setShowNewVariantForm(true)}>
            Create New Variant
          </Button>
          <Button onClick={() => navigate('/admin/leads')}>
            View Leads
          </Button>
        </div>
      </div>

      {/* Implementation Note Alert */}
      <Alert className="mb-8 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle>Implementation Update</AlertTitle>
        <AlertDescription>
          All landing page variations are now implemented as separate pages, not A/B tested. The OptimizedIndex page is now the default homepage. 
          You can still view all variations using the links in the table below.
        </AlertDescription>
      </Alert>

      {/* Date Range Selector */}
      <div className="mb-8">
        <Tabs defaultValue={dateRange} onValueChange={setDateRange}>
          <TabsList>
            <TabsTrigger value="7d">Last 7 Days</TabsTrigger>
            <TabsTrigger value="30d">Last 30 Days</TabsTrigger>
            <TabsTrigger value="90d">Last 90 Days</TabsTrigger>
            <TabsTrigger value="all">All Time</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Visitors</CardDescription>
            <CardTitle className="text-3xl">{data.totalVisitors.toLocaleString()}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Conversions</CardDescription>
            <CardTitle className="text-3xl">
              {data.variants.reduce((sum, v) => sum + v.conversions, 0).toLocaleString()}
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Average Conversion Rate</CardDescription>
            <CardTitle className="text-3xl">
              {(data.variants.reduce((sum, v) => sum + v.conversionRate, 0) / data.variants.length).toFixed(1)}%
            </CardTitle>
          </CardHeader>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Best Performing Variant</CardDescription>
            <CardTitle className="text-xl">
              {winningVariant.name}
            </CardTitle>
          </CardHeader>
          <CardFooter className="pt-0">
            <Badge className="bg-green-100 text-green-800">
              {winningVariant.conversionRate}% Conversion
            </Badge>
          </CardFooter>
        </Card>
      </div>

      {/* Statistical Significance Card */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Statistical Significance</CardTitle>
          <CardDescription>Comparing top two variants: {sortedVariants[0].name} vs {sortedVariants[1].name}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-4 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-lg font-medium">
                  {significance.hasSignificance ? 
                    'Statistically Significant Result' : 
                    'Not Yet Statistically Significant'}
                </p>
                <p className="text-gray-600">
                  {significance.message}
                </p>
              </div>
              <Badge 
                className={significance.hasSignificance ? 
                  'bg-green-100 text-green-800' : 
                  'bg-amber-100 text-amber-800'}
              >
                {(significance.confidenceLevel * 100).toFixed(0)}% Confidence
              </Badge>
            </div>
            
            <div className="flex gap-8">
              <div>
                <p className="text-sm text-gray-500">Improvement</p>
                <p className="font-medium">{significance.improvementPercentage?.toFixed(1)}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Winner</p>
                <p className="font-medium">{significance.winner ? 
                  data.variants.find(v => v.id === significance.winner)?.name : 
                  'No clear winner yet'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Sample Size</p>
                <p className="font-medium">{enoughData ? 'Sufficient' : 'Insufficient'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4">Performance Metrics</h2>
        <ABTestCharts data={data.variants} />
      </div>

      {/* Landing Page Variations */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Landing Page Variations</CardTitle>
          <CardDescription>All available landing page variations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Variant</th>
                  <th className="text-right py-3 px-4">Visitors</th>
                  <th className="text-right py-3 px-4">Conversions</th>
                  <th className="text-right py-3 px-4">Conv. Rate</th>
                  <th className="text-right py-3 px-4">Engagement</th>
                  <th className="text-right py-3 px-4">Avg Time</th>
                  <th className="text-right py-3 px-4">Bounce Rate</th>
                  <th className="text-center py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="font-medium">Optimized Index (Default)</div>
                    <div className="text-sm text-gray-500">/</div>
                  </td>
                  <td className="text-right py-3 px-4">-</td>
                  <td className="text-right py-3 px-4">-</td>
                  <td className="text-right py-3 px-4 font-medium">-</td>
                  <td className="text-right py-3 px-4">-</td>
                  <td className="text-right py-3 px-4">-</td>
                  <td className="text-right py-3 px-4">-</td>
                  <td className="text-center py-3 px-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/')}
                    >
                      View
                    </Button>
                  </td>
                </tr>
                {data.variants.map((variant) => (
                  <tr key={variant.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium">{variant.name}</div>
                      <div className="text-sm text-gray-500">/{variant.id}</div>
                    </td>
                    <td className="text-right py-3 px-4">{variant.visitors.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">{variant.conversions.toLocaleString()}</td>
                    <td className="text-right py-3 px-4 font-medium">
                      {variant.conversionRate}%
                      {variant.id === winningVariant.id && (
                        <Badge className="ml-2 bg-green-100 text-green-800">Best</Badge>
                      )}
                    </td>
                    <td className="text-right py-3 px-4">{variant.engagementRate}%</td>
                    <td className="text-right py-3 px-4">{variant.avgTimeOnPage}</td>
                    <td className="text-right py-3 px-4">{variant.bounceRate}%</td>
                    <td className="text-center py-3 px-4">
                      <div className="flex gap-2 justify-center">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/${variant.id}`)}
                        >
                          View
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle>Recommendations</CardTitle>
          <CardDescription>Based on current test results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="font-semibold mb-2">Current Implementation</h3>
              <p className="text-gray-700 mb-4">
                The OptimizedIndex page is now set as the default homepage. All landing page variations are still accessible via their dedicated URLs.
              </p>
            </div>
            
            {recommendations.map((recommendation, index) => {
              // Determine the type of recommendation
              let bgColor = 'bg-blue-50';
              let borderColor = 'border-blue-200';
              let title = 'Recommendation';
              
              if (recommendation.includes('winner')) {
                bgColor = 'bg-green-50';
                borderColor = 'border-green-200';
                title = 'Historical Data';
              } else if (recommendation.includes('Continue testing')) {
                bgColor = 'bg-amber-50';
                borderColor = 'border-amber-200';
                title = 'Continue Monitoring';
              } else if (recommendation.includes('significantly outperforming')) {
                bgColor = 'bg-purple-50';
                borderColor = 'border-purple-200';
                title = 'Create a New Variant';
              }
              
              return (
                <div key={index} className={`p-4 ${bgColor} rounded-lg border ${borderColor}`}>
                  <h3 className="font-semibold mb-2">{title}</h3>
                  <p className="text-gray-700 mb-4">{recommendation}</p>
                  
                  {title === 'Create a New Variant' && (
                    <Button variant="outline" onClick={() => setShowNewVariantForm(true)}>
                      Create New Variant
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* New Variant Form Dialog */}
      <NewVariantForm 
        isOpen={showNewVariantForm} 
        onClose={() => setShowNewVariantForm(false)} 
        onSuccess={handleVariantCreated} 
      />
    </div>
  );
};

export default ABTestDashboard; 